import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { toast } from 'react-toastify';

import { UseAppDispatch } from '@/shared/store';
import {
  EditHeader,
  PrimaryButton,
  SecondaryButton,
} from '@/shared/UI/styled/global';
import { createCompany } from '@/shared/store/actions/CompanyActionCreators';
import Loader from '@/shared/UI/elements/Loader';

import EditCompanyFormGeneral from '@/widgets/Company/DetailCompany/EditCompanyFormGeneral';
import EditCompanyFormSettings from '@/widgets/Company/DetailCompany/EditCompanyFormSettings';

const CompanyCreate = ({ companiesTree, onClose }: any) => {
  const appDispatch = UseAppDispatch();

  const refEditCompanyFormGeneral: any = useRef();
  const refEditCompanyFormSettings: any = useRef();
  const [isSubmitting, setSubmitting] = useState(false);

  const [selectedParentCompanyId, setSelectedParentCompanyId] =
    useState<any>(null);

  const [selectableCompanies, setSelectableCompanies] = useState<any[]>([]);

  const getCompaniesUpToLevelTwo = (
    nodes: any[],
    acc: any[] = [],
    level = 0,
  ): any[] => {
    if (level > 2) return acc;

    nodes.forEach(node => {
      acc.push({
        id: node.id,
        label: node.name || `Company ${node.id}`,
        level,
      });

      if (node.children?.length > 0) {
        getCompaniesUpToLevelTwo(node.children, acc, level + 1);
      }
    });

    return acc;
  };

  useEffect(() => {
    if (companiesTree?.length) {
      const list = getCompaniesUpToLevelTwo(companiesTree);
      setSelectableCompanies(list);

      if (list.length > 0) {
        setSelectedParentCompanyId(list[0].id);
      }
    }
  }, [companiesTree]);

  const handleParentCompanyChange = (event: any) => {
    setSelectedParentCompanyId(event.target.value);
  };

  const onSubmit = async () => {
    try {
      setSubmitting(true);

      const generalData = await refEditCompanyFormGeneral.current.submit();
      if (!generalData) throw 'General Data is not valid';

      const settingsData = await refEditCompanyFormSettings.current.submit();
      if (!settingsData) throw 'Settings Data is not valid';

      if (generalData && settingsData && selectedParentCompanyId) {
        const postData = Object.assign({}, generalData, settingsData, {
          parent: selectedParentCompanyId,
        });

        const responseCompany =
          (await appDispatch(createCompany(postData))) || {};
        if (!responseCompany) throw 'Create Company is failed.';

        toast('Company successfully created!');
        onClose();
      }
    } catch (err) {
      const errMessage: string = `${err}` || 'Error upon create Company';
      toast(errMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: '100%', height: 'auto' }}>
      <EditHeader>
        <Typography variant="h3">Create company</Typography>

        <Box sx={{ marginBottom: '20px' }}>
          <SecondaryButton
            onClick={() => onClose()}
            sx={{ marginRight: '15px' }}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton disabled={isSubmitting} onClick={() => onSubmit()}>
            Save
          </PrimaryButton>
        </Box>
      </EditHeader>
      <Box sx={{ marginBottom: '20px' }}>
        <FormControl fullWidth>
          <InputLabel>Parent Company</InputLabel>
          <Select
            value={selectedParentCompanyId || ''}
            onChange={handleParentCompanyChange}
            label="Parent Company"
          >
            {selectableCompanies.map(company => (
              <MenuItem
                key={company.id}
                value={company.id}
                sx={{ paddingLeft: `${company.level * 20 + 10}px` }}
              >
                {company.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <EditCompanyFormGeneral ref={refEditCompanyFormGeneral} />
      <EditCompanyFormSettings ref={refEditCompanyFormSettings} />
      <Loader
        show={isSubmitting}
        height="100%"
        width="100%"
        position="absolute"
        top="0"
        left="0"
      />
    </Box>
  );
};

export default CompanyCreate;
